import { BehaviorSubject } from 'rxjs';
import { logger } from '../logger.service';
import { Dynamics } from './dynamics';
import {
  ICallTemplateData,
  IChatPayload,
  IChatTemplateData,
  ICreateSession,
  IDirectionItem,
} from './interfaces/v2/create-session.interface';
import { IDynV2Session } from './interfaces/v2/dyn-session.interface';
import { EDynV2SessionEvent } from './enums/v2/session-event.enum';
import { ISessionPayload } from './interfaces/v2/session-payload.interface';
import { ESessionTemplate } from './enums/v2/session-template.enum';
import { ESessionType } from './enums/v2/session-type.enum';
import { typeMapper } from './mappers/v2/session-type.mapper';
import {
  ICreateNotification,
  INotificationValue,
} from './interfaces/v2/notification.interface';
import { NavigateType } from '@/components/SideBar/SideBar';
import { SolgariApiService } from '../solgari-api/solgari-api.service';
import { IParticipantEntityApi } from './interfaces/api/participant-entity.interface';
import { EEntity } from './enums/entity.enum';
import { EParticipant } from './enums/participant.enum';
import { getPluralEntityName } from './helpers/get-plural-entity-name.helper';

// Channel Integration Framework v2
export class DynamicsV2 {
  public static sessions: IDynV2Session[] = [];
  public static events$: BehaviorSubject<{
    event: EDynV2SessionEvent;
    session: IDynV2Session;
  }> = new BehaviorSubject({
    event: null,
    session: null,
  });

  public static async createChatSession(
    chatId: string,
    activityId: string,
    payload: IChatPayload,
    navigateTo: (navigateType?: NavigateType, opts?: unknown) => void,
  ) {
    if (DynamicsV2.isAlreadyExist(chatId) || Dynamics.getVersion() === 1) {
      return;
    }

    // TODO: unify template later and save owner id to store
    const ownerId = await Dynamics.getOwnerId();
    const email = SolgariApiService.getOwnEmail();
    const { source, identity, user } = payload;
    const fromParty = {
      [`partyid_${EEntity.SYSTEM_USER}@odata.bind`]: `/systemusers(${ownerId})`,
      participationtypemask: EParticipant.FROM,
    };

    const parties: IParticipantEntityApi[] = user.id
      ? [
          fromParty,
          {
            [`partyid_${user.type}@odata.bind`]: `/${getPluralEntityName(user.type)}(${user.id})`,
            participationtypemask: EParticipant.TO,
          },
        ]
      : [fromParty];

    const data: IChatTemplateData = {
      subject: `${source}: ${user.fullName}`,
      solgari_channelprovider: source,
      solgari_fromraw: email,
      solgari_toraw: identity,
      solgari_chat_activity_parties: parties,

      solgari_solgarconversationid: chatId,
    };

    const templateData = JSON.stringify(data);

    DynamicsV2.createSession(
      {
        templateName: ESessionTemplate.CHAT,
        templateParameters: {
          dataForTemplate: templateData,
          customerNumber: `${source}: ${user.fullName}`,
          dataForId: activityId,
        },
      },
      {
        id: chatId,
        navigateTo,
      },
    );
  }

  public static async createCallSession(
    sessionId: string,
    activityId: string,
    data: ICallTemplateData,
    item: IDirectionItem,
    navCallBack: () => void,
  ): Promise<string | void> {
    if (DynamicsV2.isAlreadyOneCallExist()) {
      return;
    }

    const templateData = JSON.stringify(data);

    return DynamicsV2.createSession(
      {
        templateName: ESessionTemplate.CALL,
        templateParameters: {
          dataForTemplate: templateData,
          customerNumber: item.name,
          dataForId: activityId,
        },
      },
      {
        id: sessionId,
        navigateTo: navCallBack,
      },
    );
  }

  public static async createSession(
    dynData: ICreateSession,
    payload: ISessionPayload,
  ): Promise<string | void> {
    const dyn = await Dynamics.preScriptExec();

    return dyn.createSession(dynData).then(
      (sessionId) => {
        if (!sessionId) {
          return;
        }

        DynamicsV2.addSession({
          sessionId,
          focused: false,
          type: typeMapper(dynData.templateName),
          connectedId: payload.id,
          navigateTo: payload.navigateTo,
        });

        return sessionId;
      },
      (err) => {
        logger.error('[DYNAMICS] Create session failed', err);
      },
    );
  }

  public static async createNotification(
    headerText?: string,
  ): Promise<INotificationValue | void> {
    const dyn = await Dynamics.preScriptExec();
    const cancelToken =
      'cancellationtoken' +
      Math.ceil(Math.random() * 100000 + 100000).toString();
    const input = {
      templateName: ESessionTemplate.NOTIFICATION,
      templateParameters: { headerText },
      cancellationToken: cancelToken,
    } as ICreateNotification;

    return dyn.notifyEvent(input).then(
      (res) => {
        const actionName = JSON.parse(res) as INotificationValue;

        return actionName;
      },
      (err) => {
        logger.error('[DYNAMICS (notifyEvent)]: ', err);
      },
    );
  }

  public static async getAllSessions(): Promise<string[] | void> {
    const dyn = await Dynamics.preScriptExec();

    return dyn.getAllSessions().then(
      (sessions) => {
        return sessions;
      },
      (err) => {
        logger.error('[DYNAMICS (getAllSessions)]: ', err);
      },
    );
  }

  public static async focusSession(sessionId: string): Promise<void> {
    const dyn = await Dynamics.preScriptExec();

    return dyn.requestFocusSession(sessionId).then(
      (result) => {
        return result;
      },
      (err) => {
        logger.error('[DYNAMICS] Request focus session failed ', err);
      },
    );
  }

  public static async notifyKpiBreach(sessionId: string): Promise<void> {
    const dyn = await Dynamics.preScriptExec();

    return dyn.notifyKpiBreach(sessionId).then(
      (result) => {
        return result;
      },
      (err) => {
        logger.error('[DYNAMICS] Notify KPI breach failed ', err);
      },
    );
  }

  public static addSession(session: IDynV2Session): void {
    DynamicsV2.sessions = [...DynamicsV2.sessions, session];
  }

  public static removeSession(sessionId: string): void {
    DynamicsV2.sessions = DynamicsV2.sessions.filter(
      (s) => s.sessionId !== sessionId,
    );
  }

  public static removeSessionByConnectedId(id: string): void {
    DynamicsV2.sessions = DynamicsV2.sessions.filter(
      (s) => s.connectedId !== id,
    );
  }

  public static getSessionByConnectedId(
    connectedId: string,
  ): IDynV2Session | void {
    return DynamicsV2.sessions.find((s) => connectedId === s.connectedId);
  }

  public static pathSession(params: Partial<IDynV2Session>): void {
    const session = DynamicsV2.sessions.find(
      (s) => s.sessionId === params.sessionId,
    );
    session.focused = params.focused;
  }

  public static getSession(sessionId: string): IDynV2Session {
    return DynamicsV2.sessions.find((s) => s.sessionId === sessionId);
  }

  public static isEmptySessionList(): boolean {
    return !DynamicsV2.sessions.length;
  }

  public static isAlreadyExist(connectedId: string): boolean {
    return !!DynamicsV2.sessions.find((s) => s.connectedId === connectedId);
  }

  public static isAlreadyOneCallExist(): boolean {
    return !!DynamicsV2.sessions.find((s) => s.type === ESessionType.CALL);
  }
}
